import React, { useRef, useState } from 'react';
import { Upload, FileText, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CommonAppRefreshTab() {
  const { token } = useAuth();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file.');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const submit = async () => {
    if (!file || submitting) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/common-app/admin/refresh-grid`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Refresh failed');
      setResult(data);
      toast.success(`Grid refreshed: ${data.total_rows} rows`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-common-app-tab">
      <Card>
        <CardContent className="p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-[#0F392B] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              Common App Grid — Yearly Refresh
            </h2>
            <p className="text-sm text-muted-foreground">
              Upload the latest Common App first-year requirements PDF. We&apos;ll parse it, replace the collection,
              preserve state/region enrichment, regenerate the sitemap, and log per-school diffs for change alerts.
            </p>
          </div>

          <label
            htmlFor="common-app-pdf"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`block border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-[#0F392B] bg-[#F9F9F7]' : 'border-[#E2E2E2] hover:border-[#0F392B]/40 hover:bg-[#F9F9F7]/40'
            }`}
            data-testid="common-app-dropzone"
          >
            <input
              ref={inputRef}
              id="common-app-pdf"
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
              data-testid="common-app-file-input"
            />
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <FileText className="h-8 w-8 text-[#0F392B]" strokeWidth={1.5} />
                <p className="text-sm font-semibold text-[#0F392B]">{file.name}</p>
                <p className="text-[11px] text-muted-foreground">{(file.size / 1024).toFixed(0)} KB — click to change</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
                <p className="text-sm font-semibold text-[#0F392B]">Drop the Common App PDF here</p>
                <p className="text-[11px] text-muted-foreground">or click to browse — .pdf only</p>
              </div>
            )}
          </label>

          <div className="flex items-center justify-between mt-5">
            <p className="text-[11px] text-muted-foreground italic">
              This replaces the entire collection. State/region enrichment is preserved by slug.
            </p>
            <Button
              onClick={submit}
              disabled={!file || submitting}
              className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-11 px-6 text-sm font-semibold"
              data-testid="common-app-refresh-submit"
            >
              {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Parsing…</>) : 'Refresh grid'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card data-testid="common-app-refresh-result">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="h-5 w-5 text-[#0F392B]" strokeWidth={1.75} />
              <h3 className="text-base font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>Refresh complete</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Total rows</p><p className="text-2xl font-semibold text-[#0F392B]">{result.total_rows}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Preserved states</p><p className="text-2xl font-semibold text-[#0F392B]">{result.preserved_states}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Added</p><p className="text-2xl font-semibold text-[#0F392B]">{result.added}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Removed</p><p className="text-2xl font-semibold text-[#0F392B]">{result.removed}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Changed</p><p className="text-2xl font-semibold text-[#D4AF37]">{result.changed}</p></div>
            </div>
            {result.changed > 0 && (
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-3">
                <AlertTriangle className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-[#0F392B]">
                  {result.changed} schools have changes in this refresh. A diff has been written to <code className="text-[11px] bg-white/60 px-1 rounded">common_app_change_log</code> — the change-alert worker will email subscribed watchers on the next run.
                </p>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground italic mt-4">
              Sitemap regenerated: {result.sitemap_regenerated ? 'yes' : 'no'} · Rebuild + redeploy to publish the new sitemap.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
